import React, { useEffect, useRef, useState } from "react";
import { Alert, Button, Form, Input, Space, Tag, Typography } from "antd";
import { WhatsAppOutlined } from "@ant-design/icons";
import { useAuth } from "../context/AuthContext";
import { useOptionalSelection } from "../SelectionContext";
import useBusinessResource from "../hooks/useBusinessResource";
import useRecoveryCooldown from "../hooks/useRecoveryCooldown";
import apiClient from "../api/apiClient";

export default function AccountRecoverySettings({ active }) {
  const { user } = useAuth();
  const selection = useOptionalSelection();
  const companyId = Number(selection?.selectedCompany?.id || selection?.selectedCompany || user?.company_id);
  const [form] = Form.useForm();
  const [codeForm] = Form.useForm();
  const [request, setRequest] = useState(null);
  const [busy, setBusy] = useState("");
  const [feedback, setFeedback] = useState("");
  const [error, setError] = useState("");
  const [cooldown, setCooldown] = useRecoveryCooldown();
  const scope = JSON.stringify([user?.id, companyId, Boolean(active)]);
  const currentScope = useRef(scope);
  currentScope.current = scope;
  const pending = useRef(null);
  const status = useBusinessResource({ resource: "account-recovery-settings", moduleScope: "security",
    userId: user?.id, companyId: null, warehouseId: null, scopeLevel: "user", enabled: Boolean(active && user?.id),
    active: Boolean(active), load: async (signal) => {
      const { data } = await apiClient.get("/api/password-reset/me", { signal });
      if (typeof data.verified !== "boolean") throw new Error("Statut de récupération indisponible.");
      return data;
    } });
  useEffect(() => {
    pending.current?.abort();
    pending.current = null;
    setBusy(""); setRequest(null); setFeedback(""); setError(""); setCooldown(0);
    form.resetFields();
    return () => { pending.current?.abort(); };
    // A refresh never resets entered fields. Only closing or changing account/context does.
  }, [scope, form, codeForm, setCooldown]);
  const clearCodeRequest = () => {
    setRequest(null); setFeedback(""); setError("");
  };

  const requestCode = async ({ phone, currentPassword }) => {
    if (pending.current || status.status !== "verified") return;
    const startedScope = scope;
    const controller = new AbortController();
    pending.current = controller;
    setBusy("request"); setFeedback(""); setError("");
    try {
      const { data } = await apiClient.post("/api/password-reset/me/whatsapp/request",
        { phone, currentPassword }, { signal: controller.signal });
      if (currentScope.current !== startedScope) return;
      if (!/^[a-f0-9]{48}$/.test(data.requestId || "")) throw new Error("INVALID_RESPONSE");
      setRequest(data.requestId); setCooldown(Number(data.retryAfter) || 60);
      if (request) codeForm.resetFields();
      setFeedback(data.message);
    } catch (err) {
      if (currentScope.current === startedScope && err.code !== "ERR_CANCELED") setError(err.response?.data?.message || "Le code n’a pas pu être demandé.");
    } finally {
      if (currentScope.current === startedScope) {
        form.setFieldsValue({ currentPassword: "" }); pending.current = null; setBusy("");
      }
    }
  };
  const verify = async ({ code }) => {
    if (!request || pending.current) return;
    const startedScope = scope;
    const controller = new AbortController();
    pending.current = controller; setBusy("verify"); setError("");
    try {
      const { data } = await apiClient.post("/api/password-reset/me/whatsapp/verify", { requestId: request, code }, { signal: controller.signal });
      if (currentScope.current !== startedScope) return;
      if (!data.verified) throw new Error("INVALID_RESPONSE");
      setRequest(null); codeForm.resetFields(); setFeedback("Votre numéro WhatsApp de récupération est vérifié.");
      await status.refresh({ force: true }).catch(() => {});
    } catch (err) {
      if (currentScope.current === startedScope && err.code !== "ERR_CANCELED") setError(err.response?.data?.message || "Le code n’a pas pu être vérifié.");
    } finally {
      if (currentScope.current === startedScope) { pending.current = null; setBusy(""); }
    }
  };
  return <Space direction="vertical" size={16} style={{ width: "100%" }}>
    <Typography.Paragraph>
      Vérifiez un numéro personnel pour récupérer votre accès en cas d’oubli du mot de passe.
      Ce réglage est distinct des destinataires des bilans quotidiens et ne les modifie pas.
    </Typography.Paragraph>
    {status.data && <div>
      <Tag color={status.data.verified ? "success" : "default"}>{status.data.verified ? "Numéro vérifié" : "Numéro non vérifié"}</Tag>
      {status.data.verified && <Typography.Text>{status.data.numberMasked}</Typography.Text>}
    </div>}
    {status.error && <Alert type="warning" showIcon message="Le statut n’a pas pu être actualisé. Votre saisie est conservée."
      action={<Button size="small" onClick={() => status.refresh({ force: true }).catch(() => {})}>Réessayer</Button>} />}
    {status.data && !status.data.whatsappAvailable && <Alert type="info" showIcon
      message="La vérification WhatsApp n’est pas encore activée. La récupération par email reste accessible." />}
    {feedback && <Alert type="success" showIcon message={feedback} />}
    {error && <Alert type="error" showIcon message={error} />}
    <Form form={form} layout="vertical" requiredMark={false} onFinish={requestCode}
      onValuesChange={(changed) => { if (Object.hasOwn(changed, "phone")) clearCodeRequest(); }}>
      <Form.Item name="phone" label="Numéro de récupération WhatsApp" rules={[{ required: true, message: "Renseignez votre numéro personnel avec l’indicatif du pays." }]}>
        <Input prefix={<WhatsAppOutlined />} autoComplete="tel" inputMode="tel" placeholder="Ex. +226 70 00 00 00" disabled={Boolean(busy)} />
      </Form.Item>
      {status.data?.candidateNumber && <Button type="link" style={{ paddingInline: 0 }} disabled={Boolean(busy)}
        onClick={() => { clearCodeRequest(); form.setFieldsValue({ phone: status.data.candidateNumber }); }}>Reprendre mon numéro déjà renseigné</Button>}
      <Form.Item name="currentPassword" label="Mot de passe actuel pour vérifier le numéro"
        rules={[{ required: true, message: "Confirmez votre mot de passe actuel." }]}>
        <Input.Password autoComplete="current-password" disabled={Boolean(busy)} />
      </Form.Item>
      <Button type="primary" htmlType="submit" loading={busy === "request"}
        disabled={Boolean(busy) || cooldown > 0 || status.status !== "verified" || !status.data?.whatsappAvailable}>
        {cooldown > 0 ? "Nouvel envoi dans " + cooldown + " s" : "Recevoir un code de vérification"}
      </Button>
    </Form>
    {request && <Form form={codeForm} clearOnDestroy layout="vertical" requiredMark={false} onFinish={verify}>
      <Form.Item name="code" label="Code reçu sur WhatsApp" rules={[{ required: true, message: "Renseignez le code reçu." },
        { pattern: /^\d{6}$/, message: "Le code contient 6 chiffres." }]}>
        <Input inputMode="numeric" autoComplete="one-time-code" maxLength={6} disabled={Boolean(busy)} />
      </Form.Item>
      <Typography.Paragraph type="secondary">Le code est valable 5 minutes. Un nouvel envoi remplace le précédent code.</Typography.Paragraph>
      <Button type="primary" htmlType="submit" loading={busy === "verify"} disabled={Boolean(busy)}>Confirmer mon numéro</Button>
    </Form>}
  </Space>;
}
